import { Injectable, inject, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthError, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { AUTH_CONFIG } from '@onrsr/ui/auth';
import { Observable, Subject, take } from 'rxjs';

const SIGN_IN_ERRORS: Record<string, string> = {
  [BrowserAuthErrorCodes.popupWindowError]: 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
  [BrowserAuthErrorCodes.userCancelled]: 'Sign-in was cancelled.',
};

/**
 * Re-authenticates in place when the API stops accepting the current token.
 * Callers wait on `signedIn()` while the session-expired overlay asks the user to sign in;
 * the MSAL popup flow leaves the router and every open form untouched.
 */
@Injectable({ providedIn: 'root' })
export class ReauthenticationService {
  private readonly msal = inject(MsalService);
  private readonly config = inject(AUTH_CONFIG);
  private readonly signedIn$ = new Subject<void>();

  private readonly requiredSignal = signal(false);
  private readonly busySignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly required = this.requiredSignal.asReadonly();
  readonly busy = this.busySignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  /** Flags the session as expired and resolves once the user has signed in again. */
  signedIn(): Observable<void> {
    this.requiredSignal.set(true);
    return this.signedIn$.pipe(take(1));
  }

  /** Must be called from a user gesture so the browser allows the popup. */
  signIn(): void {
    this.busySignal.set(true);
    this.errorSignal.set(null);
    this.msal.acquireTokenPopup({ scopes: this.config.scopes, account: this.msal.instance.getActiveAccount() ?? undefined }).subscribe({
      next: () => {
        this.busySignal.set(false);
        this.requiredSignal.set(false);
        this.signedIn$.next();
      },
      error: (error: unknown) => {
        this.busySignal.set(false);
        this.errorSignal.set(describeSignInError(error));
      },
    });
  }
}

function describeSignInError(error: unknown): string {
  const code = error instanceof AuthError ? error.errorCode : '';
  return SIGN_IN_ERRORS[code] ?? 'Sign-in failed. Please try again.';
}
